/**
 * API client.
 *
 * Same-origin throughout: Vite proxies /api in development, nginx proxies it in
 * production, so there is no base URL to configure per environment and nothing to
 * get wrong between them.
 */

const TOKEN_KEY = 'piq-token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private windows and blocked site data both throw here. The app still works;
    // the session just does not survive a reload.
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  // Any 401 outside the login call means the session is gone — expired, or the
  // account was deactivated, which JwtStrategy re-checks on every request. Clear
  // it rather than leaving a dead token to fail every subsequent call.
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    setToken(null);
    window.location.reload();
    throw new ApiError('Session expired', 401);
  }

  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  clientId: string | null;
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const res = await api<{ token: string; user: AuthUser }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(res.token);
  return res.user;
}
